import React from "react";
import { Img, staticFile, useCurrentFrame } from "remotion";
import { Struck } from "../../b2c/ui/bits";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { useTiming } from "../timing";
import { Headline, Split, Sub } from "../ui/Split";
import { Sfx, Ticks } from "../ui/Sfx";
import { Card, usePop } from "../ui/Surfaces";

/**
 * Pricing — the partner page's single model in one card: 20 % commission
 * on generated sales, and the three things a brand braces for (subscription,
 * setup fees, commitment) crossed out one per beat. The CMS row answers
 * "how long to plug in": Shopify, WooCommerce, PrestaShop, Magento,
 * WordPress (the partner page's logos), live in under an hour.
 */

const VISUAL = { w: 820, h: 660 };
/** Strikes on scene beats 2, 2.5 and 3, the CMS logos from beat 3.5. */
const STRIKE_BEATS = [2, 2.5, 3];
const CMS_BEAT = 3.5;

const useBeats = () => {
  const { beat } = useTiming();
  return {
    strikes: STRIKE_BEATS.map((b) => Math.round(b * beat)),
    cmsAt: Math.round(CMS_BEAT * beat),
  };
};
const CMS = ["shopify", "woo", "prestashop", "magento", "wordpress"] as const;

const PriceCard: React.FC = () => {
  const frame = useCurrentFrame();
  const { strikes, cmsAt } = useBeats();
  const copy = useCopy();
  const p = copy.pricing;
  const price = usePop(6);
  return (
    <Card style={{ width: VISUAL.w, height: VISUAL.h, padding: "48px 56px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 26 }}>
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 190,
            lineHeight: 0.86,
            letterSpacing: -9,
            color: LOOK.blue,
            scale: String(price),
            transformOrigin: "0% 100%",
          }}
        >
          {p.price}
        </div>
        <div
          style={{
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 32,
            lineHeight: 1.2,
            color: LOOK.ink,
            paddingBottom: 8,
            maxWidth: 320,
            opacity: price,
          }}
        >
          {p.priceLabel}
        </div>
      </div>

      <div
        style={{ height: 2, background: LOOK.mist, margin: "40px 0 30px" }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {p.struck.map((item, i) => {
          const at = strikes[i];
          const inP = rampIn(at - 12, frame);
          return (
            <div
              key={item}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                fontFamily: LOOK.display,
                fontWeight: LOOK.bold,
                fontSize: 44,
                letterSpacing: -1,
                color: LOOK.ink,
                opacity: inP,
                translate: `${(1 - inP) * -30}px 0`,
              }}
            >
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 44,
                  background: frame >= at ? LOOK.red : LOOK.mist,
                  color: LOOK.white,
                  fontSize: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {frame >= at ? "✕" : ""}
              </span>
              <Struck at={at} thickness={8} dimTo={0.4}>
                {item}
              </Struck>
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: "absolute",
          left: 56,
          right: 56,
          bottom: 44,
          display: "flex",
          gap: 18,
        }}
      >
        {CMS.map((name, i) => (
          <CmsLogo key={name} name={name} at={cmsAt + i * 3} />
        ))}
      </div>
    </Card>
  );
};

/** Linear 0→1 over 8 frames: the list rows need no bounce. */
const rampIn = (at: number, frame: number) =>
  Math.min(1, Math.max(0, (frame - at) / 8));

const CmsLogo: React.FC<{ name: string; at: number }> = ({ name, at }) => {
  const p = usePop(at);
  return (
    <div
      style={{
        width: 84,
        height: 84,
        borderRadius: 22,
        overflow: "hidden",
        background: LOOK.white,
        boxShadow: "0 10px 24px -10px rgba(0, 40, 143, 0.35)",
        scale: String(p),
      }}
    >
      <Img
        src={staticFile(`b2b/cms/${name}.webp`)}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export const Pricing: React.FC = () => {
  const copy = useCopy();
  const stage = useStage();
  const { strikes, cmsAt } = useBeats();
  return (
    <>
      <Split
        background={LOOK.white}
        visualSize={VISUAL}
        textH={stage.shape === "tall" ? 330 : 270}
        text={
          <>
            <Headline text={copy.pricing.headline} start={0} />
            <Sub text={copy.pricing.sub} at={cmsAt - 4} />
          </>
        }
        visual={<PriceCard />}
      />
      <Sfx sound="pop" at={6} />
      {strikes.map((at) => (
        <Sfx key={at} sound="strike" at={at} />
      ))}
      {/* Ticks at the palette's level: under the strikes, not over them. */}
      <Ticks from={cmsAt} to={cmsAt + CMS.length * 3} every={3} />
    </>
  );
};
